// What's on around Evergreen — the events /bump shows above the activities.
//
// Gathered by hand (well, by Claude) from the Evergreen Chamber calendar, Jeffco
// Library, the Morrison Holiday Bar's monthly PDF, Evergreen Audubon, EPRD,
// Red Rocks, Meetup and the venues' own pages, for October 2026. It's a
// snapshot, so every repeat carries an `until`: when the month runs out the
// weekly nights stop showing rather than asserting a schedule nobody checked.
//
// One entry is one thing you could go to:
//
//   symbol     which bump it belongs under (🏓 🔨 🏠)
//   card       the /bump activity it's an instance of, by its exact header;
//              that card lists it as one of its next dates
//   date       first (or only) day, with `recurrence` + `until` for repeats —
//              the same rules the calendar expands
//   dates      instead of a rule, a short list of specific days
//   time, end  'HH:MM', 24-hour
//   signup     { by, url, note } when you can't just turn up: tickets, RSVP,
//              registration. `by` is the last moment to do it, when known.
//   hours      instead of a date: when it's open, for the anytime places
//   confirmed  false when the 2026 listing couldn't be found and it's going on
//              past years, or the sources disagree — the page says "check first"

const CHAMBER = 'https://business.evergreenchamber.org/events/details/';
const HOLIDAY_BAR_PDF = 'https://www.morrisonholidaybar.com/images/calendar.pdf';
const RED_ROCKS = 'https://www.redrocksonline.com/events/';
const LIBRARY = 'https://jeffcolibrary.bibliocommons.com/events/';
const KADAMPA = 'https://meditationincolorado.org/evergreen';
const CAE = 'https://evergreenarts.org/events/';
const LITTLE_BEAR = 'https://downtownevergreen.com/go/little-bear-saloon';

const WEEKLY_TO_MONTH_END = { recurrence: 'weekly', until: '2026-10-31' };

// The Holiday Bar books a band Thursday to Saturday; one entry each so the name
// is the title.
const holidayBand = (date, band) => ({
    id: `holiday-band-${date}`,
    title: band,
    symbol: '🏓',
    card: 'Music festival',
    venue: 'Morrison Holiday Bar',
    town: 'Morrison',
    cost: 'free',
    date,
    time: '20:00',
    url: HOLIDAY_BAR_PDF,
    confirmed: true,
});

const redRocks = (id, title, date, time, card = 'Music festival') => ({
    id: `red-rocks-${id}`,
    title,
    symbol: '🏓',
    card,
    venue: 'Red Rocks',
    town: 'Morrison',
    cost: 'tickets',
    date,
    time,
    signup: { by: '', url: RED_ROCKS, note: 'tickets on AXS' },
    url: RED_ROCKS,
    confirmed: true,
});

export const LOCAL_EVENTS = [
    // ---------- just show up ----------
    {
        id: 'mindfulness-walk',
        title: 'Mindfulness Walk',
        symbol: '🏓',
        card: 'Hike',
        venue: 'Pine Valley Ranch',
        town: 'Pine',
        cost: 'donation',
        // Second Sunday of every month; the Chamber page's "Oct. 12" is 2025 text.
        date: '2026-10-11',
        time: '10:00',
        end: '11:00',
        recurrence: 'monthly',
        until: '2026-12-31',
        url: 'https://mymountaintown.com/community/calendar/eventdetail/372157/-/mindfulness-walk-with-peaceworks',
        confirmed: true,
    },
    {
        id: 'holiday-karaoke',
        title: 'Karaoke',
        symbol: '🏓',
        card: 'Open Mic!',
        venue: 'Morrison Holiday Bar',
        town: 'Morrison',
        cost: 'free',
        date: '2026-10-11',
        time: '20:00',
        ...WEEKLY_TO_MONTH_END,
        url: HOLIDAY_BAR_PDF,
        confirmed: true,
    },
    {
        id: 'holiday-holdem',
        title: "Texas Hold'em",
        symbol: '🏠',
        card: 'Cards',
        venue: 'Morrison Holiday Bar',
        town: 'Morrison',
        cost: 'free',
        date: '2026-10-12',
        time: '18:00',
        ...WEEKLY_TO_MONTH_END,
        url: HOLIDAY_BAR_PDF,
        confirmed: true,
    },
    {
        id: 'holiday-swing',
        title: 'Swing and blues band night',
        symbol: '🏓',
        card: 'Swing Dancing: Mercury Cafe',
        venue: 'Morrison Holiday Bar',
        town: 'Morrison',
        cost: 'free',
        date: '2026-10-13',
        time: '19:00',
        ...WEEKLY_TO_MONTH_END,
        url: HOLIDAY_BAR_PDF,
        confirmed: true,
    },
    {
        id: 'holiday-open-mic',
        title: 'Karaoke and open mic',
        symbol: '🏓',
        card: 'Open Mic!',
        venue: 'Morrison Holiday Bar',
        town: 'Morrison',
        cost: 'free',
        date: '2026-10-14',
        time: '20:00',
        ...WEEKLY_TO_MONTH_END,
        url: HOLIDAY_BAR_PDF,
        confirmed: true,
    },
    holidayBand('2026-10-15', 'The Flamingos'),
    holidayBand('2026-10-16', 'UnChained'),
    holidayBand('2026-10-17', 'Rowhouse 43'),
    holidayBand('2026-10-22', 'Chris Koltak'),
    holidayBand('2026-10-23', 'ReCyclers'),
    holidayBand('2026-10-24', 'Atlas Radio'),
    holidayBand('2026-10-29', '1st Class Bastards (80s and 90s covers)'),
    holidayBand('2026-10-30', 'Blue Ribbon Band'),
    {
        id: 'holiday-halloween',
        title: 'Halloween costume contest and disco party',
        symbol: '🏓',
        card: null,
        venue: 'Morrison Holiday Bar',
        town: 'Morrison',
        cost: 'free',
        date: '2026-10-31',
        time: '20:00',
        url: HOLIDAY_BAR_PDF,
        confirmed: true,
    },
    {
        id: 'ace-hi-karaoke',
        title: 'Karaoke',
        symbol: '🏓',
        card: 'Open Mic!',
        venue: 'Ace Hi Tavern',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-11',
        time: '20:00',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://www.goldentoday.com/',
        confirmed: true,
    },
    {
        id: 'toad-board-games',
        title: 'Board Game Night',
        symbol: '🏠',
        card: 'Azul',
        venue: 'Mountain Toad Brewing',
        town: 'Golden',
        cost: 'free · $1 off pints',
        date: '2026-10-11',
        time: '16:00',
        end: '21:00',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://www.mountaintoadbrewing.com/events',
        confirmed: true,
    },
    {
        id: 'cactus-darts',
        title: 'Dart and pool tournament',
        symbol: '🏓',
        card: 'Pool Games @ Hangar 21',
        venue: "Cactus Jack's",
        town: 'Evergreen',
        cost: '',
        date: '2026-10-13',
        time: '20:00',
        end: '23:00',
        ...WEEKLY_TO_MONTH_END,
        url: `${CHAMBER}dart-pool-tournament-new-night-10-13-2026-107041`,
        confirmed: true,
    },
    {
        id: 'cactus-jeopardy',
        title: 'Jeopardy! Bar League',
        symbol: '🏓',
        card: 'Trivia Night',
        venue: "Cactus Jack's",
        town: 'Evergreen',
        cost: 'teams',
        date: '2026-10-14',
        time: '18:30',
        end: '20:30',
        ...WEEKLY_TO_MONTH_END,
        url: `${CHAMBER}jeopardy-bar-league-10-14-2026-107640`,
        confirmed: true,
    },
    {
        id: 'cactus-open-mic',
        title: "Jonathan's Open Mic",
        symbol: '🏓',
        card: 'Open Mic!',
        venue: "Cactus Jack's",
        town: 'Evergreen',
        cost: 'free · music, stand-up, poetry',
        date: '2026-10-15',
        time: '20:00',
        end: '23:55',
        ...WEEKLY_TO_MONTH_END,
        url: `${CHAMBER}jonathan-s-open-mic-live-music-10-15-2026-107440`,
        confirmed: true,
    },
    {
        id: 'woodcellar-open-mic',
        title: 'Almighty Woodcellar Open Mic',
        symbol: '🏓',
        card: 'Open Mic!',
        venue: 'The Woodcellar',
        town: 'Bergen Park',
        cost: '',
        date: '2026-10-15',
        time: '19:00',
        url: 'https://allevents.in/evergreen/the-almighty-woodcellar-open-mic/200030719801607',
        confirmed: true,
    },
    {
        id: 'blackbird-thursday',
        title: 'Thirsty Thursday live music',
        symbol: '🏓',
        card: 'Jazz in the park',
        venue: 'Blackbird Cafe',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-15',
        time: '16:00',
        end: '20:00',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://blackbird-cafe-evergreen.com/thirsty-thursdays/',
        confirmed: true,
    },
    {
        id: 'revival-bingo',
        title: 'Free bingo',
        symbol: '🏠',
        card: 'Cards',
        venue: 'Revival Brews',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-15',
        time: '18:00',
        ...WEEKLY_TO_MONTH_END,
        url: `${CHAMBER}free-bingo-every-thursday-at-4pm-6pm-family-friendly-game-night-10-15-2026-108780`,
        confirmed: true,
    },
    {
        id: 'revival-date-night',
        title: 'Friday Night Date Night, live music',
        symbol: '🏓',
        card: 'Jazz in the park',
        venue: 'Revival Brews',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-16',
        time: '17:00',
        ...WEEKLY_TO_MONTH_END,
        url: `${CHAMBER}friday-night-date-night-at-revival-brews-every-friday-at-5pm-10-16-2026-108784`,
        confirmed: true,
    },
    {
        id: 'bivy-trivia',
        title: 'Trivia',
        symbol: '🏓',
        card: 'Trivia Night',
        venue: 'Bivy Beer Bar',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-13',
        time: '18:30',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://www.goldentoday.com/',
        confirmed: true,
    },
    {
        id: 'buffalo-rose-trivia',
        title: 'Team Trivia Tuesday',
        symbol: '🏓',
        card: 'Trivia Night',
        venue: 'Buffalo Rose',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-13',
        time: '18:30',
        end: '20:30',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://www.goldentoday.com/',
        confirmed: true,
    },
    {
        id: 'toad-trivia',
        title: 'Toad Trivia',
        symbol: '🏓',
        card: 'Trivia Night',
        venue: 'Mountain Toad Brewing',
        town: 'Golden',
        cost: '',
        date: '2026-10-20',
        time: '18:30',
        end: '20:30',
        url: 'https://www.mountaintoadbrewing.com/events',
        confirmed: true,
    },
    {
        id: 'golden-mill-trivia',
        title: 'Trivia Night',
        symbol: '🏓',
        card: 'Trivia Night',
        venue: 'The Golden Mill',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-14',
        time: '18:30',
        end: '20:30',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://www.goldentoday.com/',
        confirmed: true,
    },
    {
        id: 'barrels-open-mic',
        title: 'Open Mic',
        symbol: '🏓',
        card: 'Open Mic!',
        venue: 'Barrels & Bottles',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-14',
        time: '17:30',
        end: '20:30',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://www.goldentoday.com/',
        confirmed: true,
    },
    {
        id: 'toad-drag-bingo',
        title: 'Drag Bingo',
        symbol: '🏓',
        card: 'Comedy Club!',
        venue: 'Mountain Toad Brewing',
        town: 'Golden',
        cost: '',
        date: '2026-10-16',
        time: '19:30',
        end: '22:00',
        url: 'https://www.mountaintoadbrewing.com/events',
        confirmed: true,
    },
    {
        id: 'snowpack-cribbage',
        title: 'Cribbage night',
        symbol: '🏠',
        card: 'Cards',
        venue: 'Snowpack Taproom',
        town: 'Conifer',
        cost: '$10 buy-in, cash',
        date: '2026-10-13',
        time: '18:00',
        end: '21:00',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://mymountaintown.com/community/calendar/eventdetail/376491',
        confirmed: true,
    },
    {
        id: 'little-bear-goodwin',
        title: 'Jeff Goodwin',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'Little Bear Saloon',
        town: 'Evergreen',
        cost: '',
        date: '2026-10-14',
        time: '18:30',
        url: LITTLE_BEAR,
        confirmed: true,
    },
    {
        id: 'little-bear-lundberg',
        title: 'Jon Lundberg',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'Little Bear Saloon',
        town: 'Evergreen',
        cost: '',
        date: '2026-10-22',
        time: '20:00',
        url: LITTLE_BEAR,
        confirmed: true,
    },
    {
        id: 'little-bear-lear',
        title: 'The Stephen Lear Band',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'Little Bear Saloon',
        town: 'Evergreen',
        cost: '',
        date: '2026-10-23',
        time: '19:30',
        url: LITTLE_BEAR,
        confirmed: true,
    },
    {
        id: 'golden-mill-taylor-scott',
        title: 'Taylor Scott, live music',
        symbol: '🏓',
        card: 'Jazz in the park',
        venue: 'The Golden Mill',
        town: 'Golden',
        cost: '',
        date: '2026-10-15',
        time: '17:00',
        end: '20:00',
        url: 'https://www.visitgolden.com/events/',
        confirmed: true,
    },
    {
        id: 'woodcellar-string-band',
        title: 'Colorado Junction String Band',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'The Woodcellar',
        town: 'Bergen Park',
        cost: '',
        date: '2026-10-17',
        time: '20:00',
        end: '23:00',
        url: 'https://allevents.in/evergreen/colorado-junction-string-band-the-woodcellar/200030767796333',
        confirmed: true,
    },
    {
        id: 'woodcellar-alyssa-medina',
        title: 'Alyssa Medina & Friends',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'The Woodcellar',
        town: 'Bergen Park',
        cost: '',
        date: '2026-10-30',
        time: '20:00',
        url: 'https://allevents.in/evergreen/alyssa-medina-and-friends-the-woodcellar/200030781190807',
        confirmed: true,
    },
    {
        id: 'all-hallows-eve',
        title: "All Hallows' Eve: costume contest and tarot readings",
        symbol: '🏓',
        card: 'Psychic Tarot Reading',
        venue: 'Inspired Fitness',
        town: 'Evergreen',
        cost: 'donation',
        date: '2026-10-17',
        time: '17:00',
        end: '19:30',
        url: `${CHAMBER}all-hallows-eve-celebration-10-17-2026-108576`,
        confirmed: true,
    },
    {
        id: 'foothills-ceramics-panel',
        title: 'Artist panel: the "Structure" ceramics show',
        symbol: '🔨',
        card: 'Attend a Lecture (Public University)',
        venue: 'Foothills Art Center',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-17',
        time: '13:30',
        end: '15:30',
        url: 'https://www.visitgolden.com/events/',
        confirmed: true,
    },
    {
        id: 'cae-opening',
        title: 'Exhibition opening: "Activating the Surface"',
        symbol: '🏓',
        card: 'Art Fest',
        venue: 'Center for the Arts Evergreen',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-22',
        time: '16:00',
        end: '19:00',
        url: CAE,
        confirmed: true,
    },
    {
        id: 'hiwan-halloween',
        title: 'Hiwan Halloween: storytelling and costume parade',
        symbol: '🏠',
        card: null,
        venue: 'Hiwan Heritage Park',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-24',
        time: '13:00',
        end: '16:00',
        url: `${CHAMBER}hiwan-halloween-10-24-2026-108800`,
        confirmed: true,
    },
    {
        id: 'trick-or-treat-trail',
        title: 'Trick-or-Treat Trail, then E.T. in the park',
        symbol: '🏠',
        card: null,
        venue: 'Buchanan Park fields',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-30',
        time: '16:00',
        end: '20:00',
        url: 'https://www.evergreenrecreation.com/',
        confirmed: true,
    },
    {
        id: 'downtown-halloween-walk',
        title: 'Downtown Evergreen Halloween Walk',
        symbol: '🏠',
        card: 'Walk',
        venue: 'Main Street',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-31',
        time: '16:00',
        end: '18:00',
        url: 'https://downtownevergreen.com/events/halloween-walk',
        confirmed: true,
    },
    {
        id: 'tommyknocker-halloween',
        title: 'Halloween costume day',
        symbol: '🏓',
        card: null,
        venue: 'Tommyknocker Brewery',
        town: 'Idaho Springs',
        cost: 'free',
        date: '2026-10-31',
        time: '11:00',
        end: '20:00',
        url: 'https://www.tommyknocker.com/events',
        confirmed: true,
    },
    {
        id: 'library-ukulele',
        title: 'Ukulele Tuesdays (loaners provided)',
        symbol: '🔨',
        card: 'Library Time!',
        venue: 'Evergreen Library',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-13',
        time: '18:00',
        end: '19:30',
        recurrence: 'biweekly',
        until: '2026-10-27',
        url: `${LIBRARY}69544d746724c63d00e9638d`,
        confirmed: true,
    },
    {
        id: 'library-hiking-safety',
        title: 'Wilderness Hiking Safety talk',
        symbol: '🔨',
        card: 'Attend a Lecture (Public University)',
        venue: 'Evergreen Library',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-14',
        time: '18:00',
        end: '19:30',
        url: `${LIBRARY}6ab1b9f58cf597003f95803d`,
        confirmed: true,
    },
    {
        id: 'library-book-group',
        title: 'Evening Book Group: Dark Renaissance',
        symbol: '🏠',
        card: 'Read',
        venue: 'Evergreen Library',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-20',
        time: '18:00',
        end: '19:30',
        url: `${LIBRARY}6a335f822ea730c17ab7c84e`,
        confirmed: true,
    },
    {
        id: 'library-classic-book-group',
        title: 'Classic Book Group: The Bluest Eye',
        symbol: '🏠',
        card: 'Read',
        venue: 'Evergreen Library',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-28',
        time: '13:00',
        end: '14:30',
        url: `${LIBRARY}695492124a9fd98b7f74dc67`,
        confirmed: true,
    },
    {
        id: 'active-minds-ballot',
        title: 'Active Minds: the 2026 Colorado ballot',
        symbol: '🔨',
        card: 'Attend a Lecture (Public University)',
        venue: 'Conifer Library',
        town: 'Conifer',
        cost: 'free',
        date: '2026-10-22',
        time: '16:30',
        end: '17:30',
        url: `${LIBRARY}6aa2d7ef3dd190003e70f708`,
        confirmed: true,
    },
    {
        id: 'no-kings-rally',
        title: 'No Kings Day #4: rally and get out the vote',
        symbol: '🔨',
        card: 'Protest',
        venue: 'Ripple Pizza lot, Aspen Park',
        town: 'Conifer',
        cost: 'free',
        date: '2026-10-17',
        time: '10:00',
        end: '12:00',
        url: 'https://indivisible.org/events/conifer-aspen-park-co-no-kings-day-4-rally-and-get-the-vote-out/',
        confirmed: true,
    },
    {
        id: 'kadampa-evergreen',
        title: 'Kadampa Buddhist meditation',
        symbol: '🔨',
        card: 'Buddhist Temple: Meditation',
        venue: 'Namaste, 1260 Bergen Pkwy #210',
        town: 'Evergreen',
        cost: '$15, card only',
        // No class Oct 14 (their fall festival).
        date: '2026-10-21',
        time: '19:00',
        end: '20:15',
        ...WEEKLY_TO_MONTH_END,
        url: KADAMPA,
        confirmed: true,
    },
    {
        id: 'kadampa-golden',
        title: 'Kadampa meditation class',
        symbol: '🔨',
        card: 'Buddhist Temple: Meditation',
        venue: 'Kadampa Golden branch',
        town: 'Golden',
        cost: 'about $15',
        date: '2026-10-17',
        time: '10:30',
        end: '11:45',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://meditationincolorado.org/',
        confirmed: true,
    },
    {
        id: 'meditation-evergreen',
        title: 'Meditation Evergreen Sunday sit',
        symbol: '🔨',
        card: 'Buddhist Temple: Meditation',
        venue: 'Vertical Skills Academy, 32156 Castle Ct',
        town: 'Evergreen',
        cost: '',
        date: '2026-10-11',
        time: '09:00',
        end: '10:30',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://mindfulnessmeditationcenters.com/evergreen/?p=19',
        confirmed: false,
    },
    {
        id: 'bergen-park-church',
        title: 'Bergen Park Church (also 9:00)',
        symbol: '🔨',
        card: 'Church',
        venue: 'Bergen Park Church',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-11',
        time: '10:30',
        recurrence: 'weekly',
        until: '2026-12-31',
        url: 'https://bergenparkchurch.org/',
        confirmed: true,
    },
    {
        id: 'church-of-the-hills',
        title: 'Church of the Hills',
        symbol: '🔨',
        card: 'Church',
        venue: 'Church of the Hills',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-11',
        time: '10:00',
        recurrence: 'weekly',
        until: '2026-12-31',
        url: 'https://mymountaintown.com/community/calendar/eventdetail/305266/-/church-of-the-hills-sunday-service',
        confirmed: true,
    },
    {
        id: 'hillside-young-adults',
        title: 'Hillside young adults service',
        symbol: '🔨',
        card: 'Church',
        venue: 'Hillside Community Church',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-11',
        time: '08:45',
        ...WEEKLY_TO_MONTH_END,
        url: 'https://bible.com/events/49390384',
        confirmed: false,
    },
    {
        id: 'evergreen-amigos',
        title: 'Evergreen Amigos Spanish conversation (ages 50+)',
        symbol: '🔨',
        card: 'Japanese/Spanish Group',
        venue: 'Buchanan Park Rec Center',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-16',
        time: '15:00',
        recurrence: 'weekly',
        until: '2026-12-18',
        url: 'https://www.active.com/evergreen-co/spanish/classes/evergreen-amigos-spanish-conversations-2026',
        confirmed: true,
    },
    {
        id: 'block-therapy',
        title: 'Block Therapy',
        symbol: '🏠',
        card: 'Deep Stretching',
        venue: "Taspen's Healing Center",
        town: 'Conifer',
        cost: '',
        date: '2026-10-14',
        time: '17:30',
        end: '18:30',
        url: 'https://peaceworksinc.co/events',
        confirmed: true,
    },
    {
        id: 'dru-yoga',
        title: 'Dru Yoga with Teri',
        symbol: '🏠',
        card: 'Deep Stretching',
        venue: "Taspen's Healing Center",
        town: 'Conifer',
        cost: '',
        date: '2026-10-28',
        time: '17:30',
        end: '18:30',
        url: 'https://peaceworksinc.co/events',
        confirmed: true,
    },
    {
        id: 'disc-golf-tags',
        title: 'Disc golf tags league',
        symbol: '🏓',
        card: 'Frisbee',
        venue: 'Buchanan Park disc golf',
        town: 'Evergreen',
        cost: '',
        date: '2026-10-15',
        time: '16:00',
        end: '18:00',
        recurrence: 'weekly',
        until: '2026-10-29',
        url: 'https://udisc.com/leagues/evergreen-dgc-DpT6Gf/schedule',
        confirmed: false,
    },
    {
        id: 'birds-and-brews',
        title: 'Morning Birds & Brews',
        symbol: '🏠',
        card: 'Birdwatching',
        venue: 'Evergreen Nature Center',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-30',
        time: '08:30',
        end: '10:00',
        url: 'https://evergreenaudubon.org/events/morning-birds-brews/',
        confirmed: true,
    },
    {
        id: 'nature-center-bats',
        title: 'Brilliant Bats family program',
        symbol: '🏠',
        card: 'Birdwatching',
        venue: 'Evergreen Nature Center',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-31',
        time: '11:00',
        end: '12:00',
        url: 'https://evergreenaudubon.org/events/saturday-family-program/',
        confirmed: true,
    },
    {
        id: 'denver-astro-open-house',
        title: 'Observatory open house',
        symbol: '🏓',
        card: 'Stargazing',
        venue: 'Chamberlin Observatory',
        town: 'Denver',
        cost: 'free',
        date: '2026-10-17',
        time: '18:45',
        end: '21:45',
        url: 'https://www.denverastro.org/',
        confirmed: true,
    },

    // ---------- sign up first ----------
    {
        id: 'dracula-evenings',
        title: 'Dracula, A Comedy of Terrors',
        symbol: '🏓',
        card: 'Comedy Club!',
        venue: 'Center/Stage',
        town: 'Evergreen',
        cost: 'tickets',
        dates: ['2026-10-16', '2026-10-17', '2026-10-23', '2026-10-24'],
        time: '19:30',
        signup: { by: '', url: `${CHAMBER}dracula-a-comedy-of-terrors-presented-by-evergreen-players-10-11-2026-107947`, note: 'tickets from Evergreen Players' },
        url: `${CHAMBER}dracula-a-comedy-of-terrors-presented-by-evergreen-players-10-11-2026-107947`,
        confirmed: true,
    },
    {
        id: 'dracula-matinees',
        title: 'Dracula, A Comedy of Terrors (matinee)',
        symbol: '🏓',
        card: 'Comedy Club!',
        venue: 'Center/Stage',
        town: 'Evergreen',
        cost: 'tickets',
        dates: ['2026-10-11', '2026-10-18', '2026-10-25'],
        time: '14:00',
        signup: { by: '', url: `${CHAMBER}dracula-a-comedy-of-terrors-presented-by-evergreen-players-10-11-2026-107947`, note: 'tickets from Evergreen Players' },
        url: `${CHAMBER}dracula-a-comedy-of-terrors-presented-by-evergreen-players-10-11-2026-107947`,
        confirmed: true,
    },
    {
        id: 'charcuterie-workshop',
        title: 'Charcuterie workshop and wine tasting',
        symbol: '🔨',
        card: 'Class: Cooking',
        venue: '27965 Meadow Dr',
        town: 'Evergreen',
        cost: '',
        date: '2026-10-15',
        time: '17:30',
        signup: { by: '', url: 'https://allevents.in/evergreen/charcuterie-workshop-and-wine-tasting/200030752311539', note: 'RSVP' },
        url: 'https://allevents.in/evergreen/charcuterie-workshop-and-wine-tasting/200030752311539',
        confirmed: true,
    },
    {
        id: 'wine-women-wealth',
        title: 'Wine, Women & Wealth',
        symbol: '🔨',
        card: 'Financial Planning',
        venue: 'Connects Workspace',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-14',
        time: '18:30',
        end: '20:00',
        signup: { by: '', url: `${CHAMBER}wine-women-and-wealth-www-in-golden-10-14-2026-108640`, note: 'RSVP' },
        url: `${CHAMBER}wine-women-and-wealth-www-in-golden-10-14-2026-108640`,
        confirmed: true,
    },
    {
        id: 'nightmare-at-1520',
        title: 'Nightmare at 1520 haunted house',
        symbol: '🏓',
        card: null,
        venue: 'Buchanan Park',
        town: 'Evergreen',
        cost: '$20, more at the door',
        dates: ['2026-10-15', '2026-10-16', '2026-10-17', '2026-10-22', '2026-10-23',
            '2026-10-24', '2026-10-29', '2026-10-30', '2026-10-31'],
        time: '19:00',
        end: '21:30',
        signup: { by: '', url: 'https://events.humanitix.com/nightmare-at-1520-haunted-house', note: 'cheaper online' },
        url: `${CHAMBER}nightmare-at-1520-108794`,
        confirmed: true,
    },
    {
        id: 'little-bear-dead-panic',
        title: 'Dead Panic (Widespread Panic and Dead tribute)',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'Little Bear Saloon',
        town: 'Evergreen',
        cost: 'about $12',
        date: '2026-10-16',
        time: '20:00',
        signup: { by: '', url: 'https://allevents.in/evergreen/little-bear-live-presents-dead-panic/100002003011668821', note: 'tickets' },
        url: LITTLE_BEAR,
        confirmed: true,
    },
    {
        id: 'talking-dead-crawl',
        title: 'The Talking Dead haunted pub crawl (21+)',
        symbol: '🏓',
        card: null,
        venue: 'Downtown Golden',
        town: 'Golden',
        cost: 'tickets',
        dates: ['2026-10-16', '2026-10-17', '2026-10-23', '2026-10-24', '2026-10-30'],
        time: '18:30',
        end: '21:30',
        signup: { by: '', url: 'https://ticketbud.com/events/58223b58-94d8-11f1-960f-42010a717021', note: 'tickets on Ticketbud' },
        url: 'https://www.goldenhistorytours.com/talking-dead',
        confirmed: true,
    },
    {
        id: 'morrison-ghost-tour',
        title: 'Morrison Ghosts & Legends walking tour',
        symbol: '🏠',
        card: 'Walk',
        venue: 'Downtown Morrison',
        town: 'Morrison',
        cost: '$30',
        date: '2026-10-16',
        time: '18:30',
        end: '20:30',
        signup: { by: '', url: 'https://coloradohauntedhistory.com/tourinfo', note: 'book ahead' },
        url: 'https://mymountaintown.com/community/calendar/eventdetail/388383',
        confirmed: true,
    },
    {
        id: 'hops-drops',
        title: 'Hops Drops Music & Beer Festival',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'Evergreen Lake House',
        town: 'Evergreen',
        cost: 'from $33.85',
        date: '2026-10-17',
        time: '12:30',
        end: '17:30',
        signup: { by: '', url: 'https://allevents.in/evergreen/hops-drops-music-and-beer-festival/100001999392060475', note: 'tickets' },
        url: 'https://mymountaintown.com/community/calendar/eventdetail/388278',
        confirmed: true,
    },
    {
        id: 'chatfield-bird-walk',
        title: '3rd Saturday bird walk: Chatfield',
        symbol: '🏠',
        card: 'Birdwatching',
        venue: 'Chatfield State Park',
        town: 'Littleton',
        cost: 'free + park pass',
        date: '2026-10-17',
        time: '07:30',
        end: '11:30',
        signup: { by: '', url: 'https://evergreenaudubon.org/events/3rd-saturday-bird-walk-chatfield/', note: 'sign-up form' },
        url: 'https://evergreenaudubon.org/events/3rd-saturday-bird-walk-chatfield/',
        confirmed: true,
    },
    {
        id: 'kadampa-silent-saturday',
        title: 'Silent Saturday meditation retreat',
        symbol: '🔨',
        card: 'Buddhist Temple: Meditation',
        venue: 'Kadampa Meditation Center',
        town: 'Denver',
        cost: '',
        date: '2026-10-17',
        time: '09:00',
        end: '12:15',
        signup: { by: '', url: 'https://meditationincolorado.org/', note: 'register' },
        url: 'https://meditationincolorado.org/',
        confirmed: true,
    },
    {
        id: 'little-bear-shakedown',
        title: 'Shakedown Street, Sunday Sessions',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'Little Bear Saloon',
        town: 'Evergreen',
        cost: '$12+',
        date: '2026-10-18',
        time: '14:00',
        end: '18:00',
        signup: { by: '', url: 'https://allevents.in/evergreen/little-bear-live-presents-shakedown-street-sunday-sessions/100002003010852379', note: 'tickets' },
        url: LITTLE_BEAR,
        confirmed: true,
    },
    {
        id: 'cae-fused-glass',
        title: 'Art Bar: fused glass harvest tiles',
        symbol: '🔨',
        card: 'Class: Painting',
        venue: 'Center for the Arts Evergreen',
        town: 'Evergreen',
        cost: '$95',
        date: '2026-10-20',
        time: '18:00',
        end: '20:00',
        signup: { by: '', url: CAE, note: 'RSVP' },
        url: CAE,
        confirmed: true,
    },
    {
        id: 'cae-leather',
        title: 'Art Bar: Tandy leather gifts',
        symbol: '🔨',
        card: 'Class: Painting',
        venue: 'Center for the Arts Evergreen',
        town: 'Evergreen',
        cost: '$75',
        date: '2026-10-22',
        time: '18:00',
        end: '20:00',
        signup: { by: '', url: CAE, note: 'RSVP' },
        url: CAE,
        confirmed: true,
    },
    {
        id: 'pippa-grant',
        title: 'Author night: Pippa Grant (21+)',
        symbol: '🔨',
        card: "Writer's Conference",
        venue: 'Creekside Cellars',
        town: 'Evergreen',
        cost: '$30, book and wine included',
        date: '2026-10-22',
        time: '18:00',
        end: '20:00',
        signup: { by: '', url: 'https://allevents.in/evergreen/author-event-how-to-troll-a-guy-in-ten-days-with-pippa-grant/100001997624637064', note: 'tickets' },
        url: 'https://allevents.in/evergreen/author-event-how-to-troll-a-guy-in-ten-days-with-pippa-grant/100001997624637064',
        confirmed: true,
    },
    {
        id: 'cae-stained-glass',
        title: 'Art Bar: stained glass pumpkins',
        symbol: '🔨',
        card: 'Class: Painting',
        venue: 'Center for the Arts Evergreen',
        town: 'Evergreen',
        cost: '$95',
        date: '2026-10-23',
        time: '18:00',
        end: '20:00',
        signup: { by: '', url: CAE, note: 'RSVP' },
        url: CAE,
        confirmed: true,
    },
    {
        id: 'boological-night-hike',
        title: 'Boo-Logical Adaptations night hike',
        symbol: '🏓',
        card: 'Hike',
        venue: 'Lookout Mountain Nature Center',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-23',
        time: '19:00',
        end: '20:30',
        signup: { by: '', url: 'https://www.jeffco.us/calendar.aspx?EID=16768', note: 'register each person' },
        url: 'https://www.jeffco.us/calendar.aspx?EID=16768',
        confirmed: true,
    },
    {
        id: 'yoga-with-a-ranger',
        title: 'Yoga with a Ranger (outdoors)',
        symbol: '🏠',
        card: 'Deep Stretching',
        venue: 'Lookout Mountain Nature Center',
        town: 'Golden',
        cost: 'free',
        date: '2026-10-24',
        time: '08:30',
        end: '09:30',
        signup: { by: '', url: 'https://www.jeffco.us/calendar.aspx?EID=16769', note: 'register' },
        url: 'https://www.jeffco.us/calendar.aspx?EID=16769',
        confirmed: true,
    },
    {
        id: 'st-marys-glacier',
        title: "St. Mary's Glacier fall hike (20s–40s Meetup)",
        symbol: '🏓',
        card: 'Hike',
        venue: "St. Mary's Glacier",
        town: 'Idaho Springs',
        cost: 'free',
        date: '2026-10-24',
        time: '09:00',
        signup: { by: '', url: 'https://www.meetup.com/denverfun/events/316570866/', note: 'RSVP on Meetup' },
        url: 'https://www.meetup.com/denverfun/events/316570866/',
        confirmed: true,
    },
    {
        id: 'standley-stargazing',
        title: 'Standley Lake stargazing',
        symbol: '🏓',
        card: 'Stargazing',
        venue: 'Majestic View Nature Center',
        town: 'Arvada',
        cost: '$5 orientation',
        date: '2026-10-24',
        time: '18:00',
        end: '20:00',
        signup: { by: '', url: 'https://www.arvadaco.gov/calendar.aspx?EID=3833', note: 'register' },
        url: 'https://www.arvadaco.gov/calendar.aspx?EID=3833',
        confirmed: true,
    },
    {
        id: 'cae-clay-date',
        title: 'Clay Date: hand-building, create and sip',
        symbol: '🏓',
        card: 'Pottery / Painting!',
        venue: 'Center for the Arts Evergreen',
        town: 'Evergreen',
        cost: '$75',
        date: '2026-10-24',
        time: '18:00',
        end: '20:00',
        signup: { by: '', url: CAE, note: 'RSVP' },
        url: CAE,
        confirmed: true,
    },
    {
        id: 'little-bear-u2',
        title: 'Bullet the Blue Sky (U2 tribute)',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'Little Bear Saloon',
        town: 'Evergreen',
        cost: 'tickets',
        date: '2026-10-24',
        time: '20:00',
        signup: { by: '', url: 'https://allevents.in/evergreen/bullet-the-blue-sky-tickets-coming-soon/200030687204722', note: 'tickets coming soon' },
        url: LITTLE_BEAR,
        confirmed: true,
    },
    {
        id: 'full-moon-hike',
        title: 'Full Moon night hike, 4.25 mi',
        symbol: '🏓',
        card: 'Stargazing',
        venue: 'Alderfer/Three Sisters Park',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-25',
        time: '19:30',
        end: '22:00',
        signup: { by: '', url: 'https://www.jeffco.us/calendar.aspx?EID=16770', note: 'register' },
        url: 'https://www.jeffco.us/calendar.aspx?EID=16770',
        confirmed: true,
    },
    {
        id: 'evergreen-chorale',
        title: 'Evergreen Chorale: Double Trouble, The Dark Side',
        symbol: '🏓',
        card: null,
        venue: 'Evergreen Lutheran Church',
        town: 'Evergreen',
        cost: '$30, students $15',
        date: '2026-10-25',
        time: '15:00',
        signup: { by: '', url: 'https://ovationwest.org/', note: 'tickets' },
        url: `${CHAMBER}double-trouble-the-dark-side-of-the-evergreen-chorale-108777`,
        confirmed: true,
    },
    {
        id: 'cae-poppies',
        title: 'Art Bar: Dancing Poppies painting night',
        symbol: '🏓',
        card: 'Paint & Sip',
        venue: 'Center for the Arts Evergreen',
        town: 'Evergreen',
        cost: '$55',
        date: '2026-10-27',
        time: '18:00',
        end: '20:00',
        signup: { by: '', url: CAE, note: 'RSVP' },
        url: CAE,
        confirmed: true,
    },
    {
        id: 'mountain-gaming',
        title: 'Last-Tuesday Gaming in the Mountains',
        symbol: '🏠',
        card: 'Azul',
        venue: 'Venue in the RSVP',
        town: 'Evergreen',
        cost: 'free',
        date: '2026-10-27',
        time: '18:00',
        signup: { by: '', url: 'https://www.meetup.com/the-evergreen-board-game-group/events/315669162/', note: 'RSVP on Meetup' },
        url: 'https://www.meetup.com/the-evergreen-board-game-group/events/315669162/',
        confirmed: true,
    },
    {
        id: 'boogie-bash',
        title: 'Boo-gie Bash with Wash Park Band',
        symbol: '🏓',
        card: 'Swing Dancing: Mercury Cafe',
        venue: 'Wild Game Clubhouse',
        town: 'Bergen Park',
        cost: '$30 ahead, $45 at the door',
        date: '2026-10-30',
        time: '18:00',
        end: '22:00',
        signup: { by: '2026-10-30', url: 'https://boogieatthebarn.com', note: '$15 off ahead' },
        url: `${CHAMBER}boo-gie-bash-108613`,
        confirmed: true,
    },
    {
        id: 'zombie-run',
        title: 'EAPL Zombie Run 5K',
        symbol: '🏠',
        card: 'Walk',
        venue: 'Buchanan Park ball fields',
        town: 'Evergreen',
        cost: '$30 online, $40 race day',
        date: '2026-10-31',
        time: '10:00',
        signup: { by: '2026-10-30T10:00', url: 'https://raceroster.com/events/2026/119374/eapl-zombie-run-5k', note: 'online until Fri 10am' },
        url: 'https://eapl.com/event/2026-zombie-fun-run/',
        confirmed: true,
    },
    {
        id: 'tunisia-halloween',
        title: 'Tunisia Halloween Dance Party',
        symbol: '🏓',
        card: 'Arcade Bar',
        venue: 'The Wild Game',
        town: 'Bergen Park',
        // General admission is a free walk-in; only the VIP tables are ticketed.
        cost: 'free walk-in · tables $60–125',
        date: '2026-10-31',
        time: '19:00',
        end: '23:00',
        url: 'https://www.eventbrite.com/e/tunisia-live-at-the-wild-game-the-halloween-dance-party-tickets-2002646689157',
        confirmed: true,
    },
    {
        id: 'heritage-hall-halloween',
        title: 'Halloween at Heritage Hall (21+)',
        symbol: '🏓',
        card: null,
        venue: 'Black Hat Cattle Co',
        town: 'Kittredge',
        cost: '$75, drink and buffet',
        date: '2026-10-31',
        time: '20:00',
        end: '23:59',
        signup: { by: '', url: 'https://blackhatcattle.co/events/halloween-at-heritage-hall', note: 'tickets' },
        url: 'https://blackhatcattle.co/events/halloween-at-heritage-hall',
        confirmed: true,
    },
    {
        id: 'little-bear-halloween',
        title: 'Halloween with FinalEyes',
        symbol: '🏓',
        card: 'Music festival',
        venue: 'Little Bear Saloon',
        town: 'Evergreen',
        cost: 'tickets',
        date: '2026-10-31',
        time: '20:00',
        signup: { by: '', url: 'https://allevents.in/evergreen/halloween-w-finaleyes-tickets-coming-soon/200030653128834', note: 'tickets coming soon' },
        url: LITTLE_BEAR,
        confirmed: true,
    },
    redRocks('taking-back-sunday', 'Taking Back Sunday, Thrice', '2026-10-14', '19:00'),
    redRocks('matt-rife', 'Matt Rife: Stay Golden', '2026-10-18', '19:00', 'Comedy Club!'),
    redRocks('macklemore', 'Macklemore', '2026-10-22', '19:00'),
    redRocks('stardew', 'Stardew Valley: Symphony of Seasons', '2026-10-25', '19:30'),
    redRocks('haunted-rocks', 'Haunted Rocks: Cypress Hill, Method Man & Redman', '2026-10-29', '18:00'),
    redRocks('deadmau5-fri', 'deadmau5', '2026-10-30', '19:30'),
    redRocks('deadmau5-sat', 'deadmau5', '2026-10-31', '19:30'),

    // ---------- anytime ----------
    {
        id: 'marshdale-pickleball',
        title: 'Marshdale pickleball, 6 courts',
        symbol: '🏓',
        card: 'Pickle Ball',
        venue: 'Marshdale Park',
        town: 'Evergreen',
        cost: 'free',
        hours: 'Daily, sunrise–sunset',
        url: 'https://www.evergreenrecreation.com/adult-pickleball',
        confirmed: true,
    },
    {
        id: 'wulf-pickleball',
        title: 'Indoor pickleball',
        symbol: '🏓',
        card: 'Pickle Ball',
        venue: 'Wulf Rec Center',
        town: 'Evergreen',
        cost: '$8 resident, $12 visitor',
        hours: 'Drop-in blocks; reserve 3 days ahead',
        url: 'https://www.evergreenrecreation.com/adult-pickleball',
        confirmed: true,
    },
    {
        id: 'wulf-tennis',
        title: 'Tennis courts',
        symbol: '🏓',
        card: 'Tennis',
        venue: 'Wulf Rec Center',
        town: 'Evergreen',
        cost: '',
        hours: 'Outside reserved times',
        url: 'https://www.evergreenrecreation.com/wulf-rec-center-tennis-court-schedule',
        confirmed: true,
    },
    {
        id: 'indian-hot-springs',
        title: 'Indian Hot Springs',
        symbol: '🏓',
        card: 'Drive to Hot Springs!',
        venue: 'Indian Hot Springs',
        town: 'Idaho Springs',
        cost: 'about $20–36',
        hours: 'Daily, about 9am–10pm',
        url: 'https://indianhotsprings.com/',
        confirmed: false,
    },
    {
        id: 'buchanan-disc-golf',
        title: 'Buchanan disc golf, 9 holes',
        symbol: '🏓',
        card: 'Frisbee',
        venue: 'Buchanan Park',
        town: 'Evergreen',
        cost: 'free',
        hours: 'Daily, dawn–dusk',
        url: 'https://udisc.com/places/us/colorado/evergreen-co',
        confirmed: true,
    },
    {
        id: 'beaver-ranch-disc-golf',
        title: 'Beaver Ranch disc golf, 21 holes',
        symbol: '🏓',
        card: 'Frisbee',
        venue: 'Beaver Ranch',
        town: 'Conifer',
        cost: 'free',
        hours: 'Daily, dawn–dusk',
        url: 'https://udisc.com/places/us/colorado/conifer-co',
        confirmed: true,
    },
    {
        id: 'evergreen-golf',
        title: 'Evergreen Golf Course',
        symbol: '🏓',
        card: 'Golf into the lake!',
        venue: 'Evergreen Golf Course',
        town: 'Evergreen',
        cost: 'green fees',
        hours: 'Tee times daily, season ends mid-November',
        until: '2026-11-15',
        url: 'https://www.cityofdenvergolf.com/evergreen-golf-course/',
        confirmed: false,
    },
    {
        id: 'wild-game',
        title: 'The Wild Game: arcade, bowling, bar',
        symbol: '🏓',
        card: 'Arcade Bar',
        venue: 'The Wild Game',
        town: 'Bergen Park',
        cost: 'pay per play',
        hours: 'Daily; call (720) 630-8888 for hours',
        url: 'https://thewildgameevergreen.com/',
        confirmed: true,
    },
    {
        id: 'maxwell-falls',
        title: 'Maxwell Falls trail',
        symbol: '🏓',
        card: 'Go Chasing Waterfalls',
        venue: 'Maxwell Falls trailhead',
        town: 'Evergreen',
        cost: 'free',
        hours: 'Daily, daylight',
        url: 'https://www.fs.usda.gov/',
        confirmed: false,
    },
    {
        id: 'lookout-mountain-preserve',
        title: 'Lookout Mountain Preserve trails',
        symbol: '🏓',
        card: 'Hike',
        venue: 'Lookout Mountain Nature Center',
        town: 'Golden',
        cost: 'free',
        hours: 'An hour before sunrise to an hour after sunset',
        url: 'https://www.jeffco.us/1281/Lookout-Mountain-Preserve-and-Nature-Center',
        confirmed: true,
    },
    {
        id: 'elk-evergreen-lake',
        title: 'Elk at Evergreen Lake',
        symbol: '🏓',
        card: 'Mountain Drive + Photography',
        venue: 'Evergreen Lake, Elk Meadow',
        town: 'Evergreen',
        cost: 'free',
        hours: 'Dawn and dusk; the rut tails off mid-October',
        until: '2026-10-25',
        url: 'https://evergreenaudubon.org/wildlife-watch/',
        confirmed: false,
    },
    {
        id: 'mount-blue-sky',
        title: 'Echo Lake and the Mount Blue Sky road',
        symbol: '🏓',
        card: 'Mountain Drive + Photography',
        venue: 'Echo Lake',
        town: 'Idaho Springs',
        cost: 'rec fee',
        hours: 'Until snow closes it; summit road already closed',
        until: '2026-11-15',
        url: 'https://www.fs.usda.gov/',
        confirmed: false,
    },
    {
        id: 'evergreen-nature-center',
        title: 'Evergreen Nature Center',
        symbol: '🏠',
        card: 'Birdwatching',
        venue: 'Evergreen Nature Center',
        town: 'Evergreen',
        cost: 'free',
        hours: 'Sat–Sun, 10am–4pm',
        url: 'https://evergreenaudubon.org/',
        confirmed: false,
    },
    {
        id: 'go-paint',
        title: 'Go Paint pottery studio',
        symbol: '🏓',
        card: 'Pottery / Painting!',
        venue: 'Go Paint',
        town: 'Evergreen',
        cost: 'pay per piece',
        hours: 'Studio hours',
        url: 'https://www.placefull.com/go-paint-evergreen-co',
        confirmed: false,
    },
    {
        id: 'pinnacle-climbing',
        title: 'The Pinnacle climbing wall',
        symbol: '🔨',
        card: 'Gym',
        venue: 'Buchanan Park Rec Center',
        town: 'Evergreen',
        cost: '$8 resident, $12 visitor',
        hours: 'Open-climb hours on the rec calendar',
        url: 'https://www.evergreenrecreation.com/the-pinnacle-at-buchanan-park-rec-center-climbing-schedule',
        confirmed: true,
    },
    {
        id: 'rec-center-gym',
        title: 'Rec center gym and classes',
        symbol: '🔨',
        card: 'Gym',
        venue: 'Wulf and Buchanan Rec Centers',
        town: 'Evergreen',
        cost: '$8 resident, $12 visitor',
        hours: 'Rec center hours',
        url: 'https://www.evergreenrecreation.com/facility-admission-fees',
        confirmed: true,
    },
    {
        id: 'zen-center-denver',
        title: 'Daily zazen',
        symbol: '🔨',
        card: 'Buddhist Temple: Meditation',
        venue: 'Zen Center of Denver',
        town: 'Denver',
        cost: '',
        hours: 'Daily sits, no sign-up',
        url: 'https://zencenterofdenver.org/events/list/',
        confirmed: true,
    },
];
